/**
 * Error model matching the backend's RFC 7807 `application/problem+json`
 * responses (ProblemDetailsMiddleware) — NOT the old `{isSuccess, data}` wrapper.
 */

export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  errors?: Array<{ field: string; message: string }> | null;
  traceId?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails;
  readonly correlationId: string | null;

  constructor(status: number, problem: ProblemDetails, correlationId: string | null = null) {
    super(problem.title ?? `Request failed (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
    this.correlationId = correlationId;
  }

  /** Short slug from the problem `type` URI, e.g. "validation-failed". */
  get code(): string {
    return this.problem.type?.split('/').pop() ?? 'unknown';
  }
}

export class ValidationError extends ApiError {
  readonly fieldErrors: Array<{ field: string; message: string }>;
  constructor(problem: ProblemDetails, correlationId: string | null) {
    super(400, problem, correlationId);
    this.name = 'ValidationError';
    this.fieldErrors = problem.errors ?? [];
  }
}

export class AuthError extends ApiError {
  constructor(status: 401 | 403, problem: ProblemDetails, correlationId: string | null) {
    super(status, problem, correlationId);
    this.name = 'AuthError';
  }
}

export class RateLimitError extends ApiError {
  constructor(problem: ProblemDetails, correlationId: string | null) {
    super(429, problem, correlationId);
    this.name = 'RateLimitError';
  }
}

export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('Network request failed — is the gateway reachable?');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

export function errorFromResponse(status: number, body: unknown, correlationId: string | null): ApiError {
  const problem: ProblemDetails =
    body && typeof body === 'object' ? (body as ProblemDetails) : { title: String(body ?? ''), status };
  if (status === 400 && problem.errors?.length) return new ValidationError(problem, correlationId);
  if (status === 401 || status === 403) return new AuthError(status, problem, correlationId);
  if (status === 429) return new RateLimitError(problem, correlationId);
  return new ApiError(status, problem, correlationId);
}

/** HTTP status → message safe to show end users. */
export function userMessage(error: unknown): string {
  if (error instanceof ValidationError) {
    return error.fieldErrors[0]?.message ?? 'Please check the highlighted fields.';
  }
  if (error instanceof RateLimitError) return 'Too many requests — please wait a moment and try again.';
  if (error instanceof AuthError) {
    return error.status === 401 ? 'Your session has expired. Please sign in again.' : "You don't have permission to do that.";
  }
  if (error instanceof ApiError) {
    if (error.status === 404) return "We couldn't find what you were looking for.";
    if (error.status === 409) return error.problem.title ?? 'That conflicts with the current state — please refresh and retry.';
    return error.problem.title ?? 'Something went wrong. Please try again.';
  }
  if (error instanceof NetworkError) return 'Connection problem — check your internet and try again.';
  return 'Something went wrong. Please try again.';
}
